/**
 * SM-2 review scheduling — §10.4.
 *
 * Scheduled PER MUSHAF PAGE, not per ayah. Hifz is revised in pages and juzʾ, and
 * 6,236 ayah-level cards would make the queue unusable where 604 maps onto how the
 * material is actually reviewed.
 *
 * SM-2 rather than FSRS: FSRS is better calibrated but needs a trained weight set and
 * a review history to fit it; SM-2 is self-contained, well understood, and its
 * interval growth already matches the classical tiers.
 */
import {
  DAY_MS,
  SRS_DEFAULT_EASE,
  SRS_LAPSE_EASE_PENALTY,
  SRS_MIN_EASE,
  TIER_SABQI_MAX_INTERVAL,
  TIER_SABQ_MAX_INTERVAL,
} from './constants'
import type { SrsCard, SrsTier } from './types'

/** Integer and timezone-free. */
export const dayStamp = (nowMs: number): number => Math.floor(nowMs / DAY_MS)

export const dayStampToMs = (day: number): number => day * DAY_MS

export function newCard(page: number, now: number): SrsCard {
  return { page, ease: SRS_DEFAULT_EASE, interval: 0, reps: 0, lapses: 0, due: dayStamp(now) }
}

/** The four SM-2 qualities. Their labels are Arabic copy — `content/srs.ts` (I3). */
export type Quality = 0 | 3 | 4 | 5

/**
 * The order the grade buttons are offered in: hardest first, so «نسيت» is nearest the
 * thumb. It is an ordering decision, not wording, so it stays here.
 */
export const GRADE_QUALITIES: readonly Quality[] = [0, 3, 4, 5]

/**
 * KNOWN RISK — "ease hell". This is faithful SM-2, so quality 4 moves ease by exactly
 * `0.1 - (0.08 + 0.02) = 0`, and repeated lapses ratchet ease down to the 1.3 floor
 * with NO PATH BACK. On mutashābihāt pages this is likely. The programme's daily
 * ceiling masks the symptom by bounding the pile but does not fix the cause. If this
 * is ever fixed, the options are ease recovery after N clean reps on `good`, or
 * switching to FSRS. Deliberately left faithful here (§19).
 */
export function grade(card: SrsCard, quality: Quality, now: number): SrsCard {
  const today = dayStamp(now)
  const next: SrsCard = { ...card, reps: card.reps + 1 }

  if (quality < 3) {
    // The lapse collapsing the interval to 0 is the entire point of tracking
    // revision: a page you blanked on should not be weeks away.
    next.lapses = card.lapses + 1
    next.interval = 0
    next.ease = Math.max(SRS_MIN_EASE, card.ease - SRS_LAPSE_EASE_PENALTY)
    next.due = today
    return next
  }

  const q = quality
  next.ease = Math.max(SRS_MIN_EASE, card.ease + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)))
  next.interval =
    card.interval === 0 ? 1 : card.interval === 1 ? 6 : Math.round(card.interval * next.ease)
  next.due = today + next.interval
  return next
}

/** Tiers, read off the CURRENT interval. */
export function tierOf(card: Pick<SrsCard, 'interval'>): SrsTier {
  if (card.interval <= TIER_SABQ_MAX_INTERVAL) return 'sabq'
  if (card.interval <= TIER_SABQI_MAX_INTERVAL) return 'sabqi'
  return 'dhor'
}

/** Cards due today or earlier, sorted by due ascending, then lapses DESCENDING, then page. */
export function dueCards(cards: Iterable<SrsCard>, now: number): SrsCard[] {
  const today = dayStamp(now)
  return [...cards]
    .filter((c) => c.due <= today)
    .sort((a, b) => a.due - b.due || b.lapses - a.lapses || a.page - b.page)
}
